import { useQuery } from '@apollo/client';
import { CircularProgress, Icon, Tooltip } from '@elipssolution/harfang';
import { mdiAlertCircle } from '@mdi/js';
import { ButtonBase, List, ListItem, ListItemText, Stack, Typography, alpha, styled } from '@mui/material';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Empty from './Empty';
import useCustomRouter from '../../../../src/hooks/useCustomRouter';
import { WidgetComponentProps } from '../../../../types/widget';
import { FETCH_DOCUMENT_UPLOADS, FetchDocumentUploadsType } from '../../api/documentUpload';
import StatusIndicator from '../../components/StatusIndicator';
import { DocumentUploadStatusEnum } from '../../types/documentUpload';

const Container = styled('div')(({ theme: { palette } }) => ({
	position: 'relative',
	overflowX: 'auto',
	flex: 1,

	'&.error': {
		backgroundColor: `${alpha(palette.error.main, 0.05)}`,
	},
}));

const LoadingWrapper = styled('div')(({ theme: { palette } }) => ({
	flex: 1,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: palette.grey[400],
}));

const ErrorWrapper = styled('div')(({ theme: { palette, spacing } }) => ({
	flex: 1,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: spacing(0.5),
	color: palette.error.main,
}));

const EmptyWrapper = styled('div')(({ theme: { palette } }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	color: palette.grey[400],
}));

const MoreLinkWrapper = styled('div')({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const SeeMoreItem = styled(ButtonBase)(({ theme: { shape, spacing } }) => ({
	padding: spacing(2, 1),
	width: '100%',
	borderBottomLeftRadius: shape.borderRadius,
	borderBottomRightRadius: shape.borderRadius,
}));

const ItemText = styled(ListItemText)({
	'& > *': {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

let resizeTimer: ReturnType<typeof setTimeout>;

const DesktopDocumentUploadsHistory = ({ readOnly }: WidgetComponentProps) => {
	const { push } = useCustomRouter();

	const wrapperRef = useRef<HTMLDivElement>(null);

	const [queryLimit, setQueryLimit] = useState<number>();

	const { data, loading, called, error } = useQuery<FetchDocumentUploadsType>(FETCH_DOCUMENT_UPLOADS, {
		notifyOnNetworkStatusChange: true,
		skip: readOnly || !queryLimit,
		variables: {
			page: {
				limit: queryLimit,
			},
		},
	});

	const documents = useMemo(
		() =>
			!readOnly
				? data?.document_documentUploads.items ?? []
				: Array(queryLimit)
						.fill(null)
						.map((_, index) => ({ id: index, status: DocumentUploadStatusEnum.SENT, name: 'Document.pdf' })),
		[data, readOnly, queryLimit],
	);

	const resizeObserver = useMemo(
		() =>
			new ResizeObserver((entries) => {
				const { height } = entries[0].contentRect;
				const limit = Math.floor(height / 49);

				if (called) {
					clearTimeout(resizeTimer);

					resizeTimer = setTimeout(() => {
						setQueryLimit((prevQueryLimit) => (prevQueryLimit !== limit ? limit : prevQueryLimit));
					}, 300);
				} else {
					setQueryLimit((prevQueryLimit) => (prevQueryLimit !== limit ? limit : prevQueryLimit));
				}
			}),
		[called],
	);

	const handleDocumentsNavigation = useCallback(() => {
		push('/document/storageUpload').catch((navigationError) => {
			throw navigationError;
		});
	}, [push]);

	// Check for ref height changes and actualize query limit
	useEffect(() => {
		if (!wrapperRef.current) return undefined;

		resizeObserver.observe(wrapperRef.current);

		return () => resizeObserver.disconnect();
	}, [resizeObserver, queryLimit, wrapperRef]);

	return (
		<Container className={clsx({ error })} ref={wrapperRef}>
			<Stack justifyContent="space-between" position="absolute" height="100%" width="100%">
				{loading && (
					<LoadingWrapper>
						<CircularProgress color="inherit" />
					</LoadingWrapper>
				)}

				{!!error && (
					<ErrorWrapper>
						<Typography>Erreur</Typography>

						<Tooltip content="Impossible de charger les documents" color="error">
							<Icon path={mdiAlertCircle} />
						</Tooltip>
					</ErrorWrapper>
				)}

				{!loading && !error && (
					<>
						<List disablePadding>
							{documents.map(({ id, status, name }) => (
								<ListItem key={id} divider>
									<ItemText primary={name} />
									<StatusIndicator status={status} />
								</ListItem>
							))}
						</List>

						{documents.length === 0 && (
							<EmptyWrapper>
								<Empty />
								Aucun document
							</EmptyWrapper>
						)}

						<MoreLinkWrapper>
							<SeeMoreItem onClick={handleDocumentsNavigation} disabled={readOnly}>
								<Typography variant="body2" fontWeight={500}>
									Voir plus
								</Typography>
							</SeeMoreItem>
						</MoreLinkWrapper>
					</>
				)}
			</Stack>
		</Container>
	);
};

export default DesktopDocumentUploadsHistory;
